/**
 * Portfolio-level cost charts (combined buildings + transport).
 * Annual cash flow: capital rust, buildings blue/magenta, transport teal/amber, net slate. Summary cards use FINANCIAL_COLORS.
 */
import React, { useMemo } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  BarElement,
  BarController,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { currencyShort, FINANCIAL_COLORS } from './portfolioCostUtils';
import type { BuildingsCostResults } from '../../services/buildingsCostModel';
import type { TransportCostResults } from '../../services/transportCostModel';
import { legendLabelsReflectLineDash } from './chartJsLegendLineDash';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  BarElement,
  BarController,
  Tooltip,
  Legend,
  Filler
);

const axisTick = (tickValue: string | number): string | number =>
  typeof tickValue === 'number' ? tickValue.toLocaleString(undefined, { maximumFractionDigits: 0 }) : tickValue;

/** Build combined series by year from buildings + transport. */
export function usePortfolioSeries(
  buildings: BuildingsCostResults | null,
  transport: TransportCostResults | null
) {
  return useMemo(() => {
    const years: number[] = [];
    const bCumNet = new Map<number, number>();
    const bCapex = new Map<number, number>();
    const bSavings = new Map<number, number>();
    const tCumNet = new Map<number, number>();
    const tFuelSavings = new Map<number, number>();

    if (buildings?.cumulativeNetCostSeries?.length) {
      buildings.cumulativeNetCostSeries.forEach((r) => {
        years.push(r.year);
        bCumNet.set(r.year, r.value);
      });
      buildings.annualCapitalCostSeries?.forEach((r) => bCapex.set(r.year, r.value));
      buildings.annualSavingsSeries?.forEach((r) => bSavings.set(r.year, r.value));
    }
    if (transport?.cumulativeNetCostSeries?.length) {
      transport.cumulativeNetCostSeries.forEach((r) => {
        if (!years.includes(r.year)) years.push(r.year);
        tCumNet.set(r.year, r.value);
      });
      transport.annualFuelSavingsSeries?.forEach((r) => tFuelSavings.set(r.year, r.value));
    }
    years.sort((a, b) => a - b);
    if (years.length === 0) return null;

    const getPrev = (m: Map<number, number>, y: number) => {
      const i = years.indexOf(y);
      return i > 0 ? m.get(years[i - 1]) ?? 0 : 0;
    };
    const tCapexByYear = new Map<number, number>();
    years.forEach((y) => {
      const cum = tCumNet.get(y) ?? 0;
      const prevCum = getPrev(tCumNet, y);
      const fuel = tFuelSavings.get(y) ?? 0;
      tCapexByYear.set(y, cum - prevCum + fuel);
    });

    const cumulativeNetCost = years.map((y) => (bCumNet.get(y) ?? 0) + (tCumNet.get(y) ?? 0));
    const cumulativeCapex = years.map((y) => {
      let bSum = 0;
      let tSum = 0;
      for (const yy of years) {
        if (yy > y) break;
        bSum += bCapex.get(yy) ?? 0;
        tSum += tCapexByYear.get(yy) ?? 0;
      }
      return bSum + tSum;
    });
    const cumulativeSavings = years.map((y) => {
      let bSum = 0;
      let tSum = 0;
      for (const yy of years) {
        if (yy > y) break;
        bSum += bSavings.get(yy) ?? 0;
        tSum += tFuelSavings.get(yy) ?? 0;
      }
      return bSum + tSum;
    });
    const annualCapex = years.map((y) => (bCapex.get(y) ?? 0) + (tCapexByYear.get(y) ?? 0));
    const annualSavings = years.map((y) => (bSavings.get(y) ?? 0) + (tFuelSavings.get(y) ?? 0));
    // UI sign convention: positive values mean net savings.
    // net = operating impact (savings) - capital exp.
    const annualNetCashFlow = years.map((y, i) => annualSavings[i] - annualCapex[i]);

    return {
      years,
      labels: years.map(String),
      cumulativeNetCost,
      cumulativeCapex,
      cumulativeSavings,
      annualCapex,
      annualSavings,
      annualNetCashFlow,
      buildingsCapex: years.map((y) => bCapex.get(y) ?? 0),
      buildingsSavings: years.map((y) => bSavings.get(y) ?? 0),
      transportCapex: years.map((y) => tCapexByYear.get(y) ?? 0),
      transportSavings: years.map((y) => tFuelSavings.get(y) ?? 0),
    };
  }, [buildings, transport]);
}

export interface PortfolioCostChartsProps {
  buildings: BuildingsCostResults | null;
  transport: TransportCostResults | null;
}

/** A. Cumulative Net Cost (line) with optional cumulative capital exp. and savings. */
export const PortfolioCumulativeNetCostChart: React.FC<PortfolioCostChartsProps> = ({ buildings, transport }) => {
  const series = usePortfolioSeries(buildings, transport);
  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: false,
            boxWidth: 28,
            generateLabels: legendLabelsReflectLineDash,
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${currencyShort(Number(ctx.parsed.y))} (positive = net savings)`,
          },
        },
      },
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: {
          title: { display: true, text: 'Cumulative $' },
          ticks: { callback: axisTick },
        },
      },
    }),
    []
  );

  if (!series || series.years.length === 0) return null;
  const hasData =
    series.cumulativeNetCost.some((v) => Math.abs(v) >= 1) ||
    series.cumulativeCapex.some((v) => Math.abs(v) >= 1) ||
    series.cumulativeSavings.some((v) => Math.abs(v) >= 1);
  if (!hasData) return null;

  const data = {
    labels: series.labels,
    datasets: [
      { label: 'Cumulative net cost', data: series.cumulativeNetCost, borderColor: FINANCIAL_COLORS.neutral, backgroundColor: 'rgba(31, 41, 55, 0.08)', fill: true, tension: 0.2, pointRadius: 2 },
      { label: 'Cumulative capital exp.', data: series.cumulativeCapex, borderColor: FINANCIAL_COLORS.unfavorable, borderDash: [4, 2], tension: 0.2, pointRadius: 1 },
      { label: 'Cumulative savings', data: series.cumulativeSavings, borderColor: FINANCIAL_COLORS.favorable, borderDash: [4, 2], tension: 0.2, pointRadius: 1 },
    ],
  };

  return (
    <div style={{ marginTop: 16 }}>
      <h5 style={{ marginBottom: 8 }}>Cumulative Net Cost</h5>
      <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: 8 }}>Combined buildings + transport. Negative = net savings.</p>
      <div style={{ height: 260 }}>
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

/** Buildings utility: blue = savings vs BAU, rose = higher bills (distinct from capital rust). */
const BLDG_UTILITY_SAVE = 'rgba(59, 130, 246, 0.88)';
const BLDG_UTILITY_COST = 'rgba(219, 39, 119, 0.88)';
const BLDG_UTILITY_BORDER_SAVE = 'rgb(29, 78, 216)';
const BLDG_UTILITY_BORDER_COST = 'rgb(157, 23, 77)';

/** Transport fuel: teal = savings vs BAU, amber = higher cost (distinct from buildings). */
const TRANS_FUEL_SAVE = 'rgba(20, 184, 166, 0.88)';
const TRANS_FUEL_COST = 'rgba(245, 158, 11, 0.9)';
const TRANS_FUEL_BORDER_SAVE = 'rgb(15, 118, 110)';
const TRANS_FUEL_BORDER_COST = 'rgb(180, 83, 9)';

const CAPITAL_BAR = 'rgba(194, 65, 12, 0.88)';
const NET_CASH_BAR = 'rgba(51, 65, 85, 0.88)';

const buildingsOpLegend = 'Buildings utility (vs BAU)';
const transportOpLegend = 'Transport fuel (vs BAU)';

function AnnualCashFlowCustomLegend() {
  const sw = (fill: string, border?: string) => (
    <span
      style={{
        display: 'inline-block',
        width: 14,
        height: 14,
        borderRadius: 2,
        background: fill,
        border: border ? `1px solid ${border}` : undefined,
        verticalAlign: 'middle',
        flexShrink: 0,
      }}
    />
  );
  const row = (children: React.ReactNode) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{children}</span>
  );
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px 20px',
        fontSize: '0.8125rem',
        color: FINANCIAL_COLORS.muted,
        marginTop: 10,
        alignItems: 'center',
      }}
      aria-label="Annual cash flow chart legend"
    >
      {row(
        <>
          {sw(CAPITAL_BAR)}
          <span>
            <strong>Capital</strong> (rust) — investment (−)
          </span>
        </>
      )}
      {row(
        <>
          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            {sw(BLDG_UTILITY_SAVE, BLDG_UTILITY_BORDER_SAVE)}
            {sw(BLDG_UTILITY_COST, BLDG_UTILITY_BORDER_COST)}
          </span>
          <span>
            <strong>Buildings utility</strong> — blue = savings vs BAU, magenta = higher bills
          </span>
        </>
      )}
      {row(
        <>
          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            {sw(TRANS_FUEL_SAVE, TRANS_FUEL_BORDER_SAVE)}
            {sw(TRANS_FUEL_COST, TRANS_FUEL_BORDER_COST)}
          </span>
          <span>
            <strong>Transport fuel</strong> — teal = savings vs BAU, amber = higher cost (zero = no bar)
          </span>
        </>
      )}
      {row(
        <>
          {sw(NET_CASH_BAR)}
          <span>
            <strong>Net</strong> (slate) — operating (buildings + transport) − capital
          </span>
        </>
      )}
    </div>
  );
}

/** B. Annual Cash Flow: capital, buildings operating, transport operating, net (portfolio = buildings + transport). */
export const PortfolioAnnualCashFlowChart: React.FC<PortfolioCostChartsProps> = ({ buildings, transport }) => {
  const series = usePortfolioSeries(buildings, transport);
  const annualCapexDisplay = series ? series.annualCapex.map((v) => -Math.abs(v)) : [];
  const buildingsOpColors = series
    ? series.buildingsSavings.map((v) => (v >= 0 ? BLDG_UTILITY_SAVE : BLDG_UTILITY_COST))
    : [];
  const buildingsOpBorders = series
    ? series.buildingsSavings.map((v) => (v >= 0 ? BLDG_UTILITY_BORDER_SAVE : BLDG_UTILITY_BORDER_COST))
    : [];
  const transportOpColors = series
    ? series.transportSavings.map((v) => (v >= 0 ? TRANS_FUEL_SAVE : TRANS_FUEL_COST))
    : [];
  const transportOpBorders = series
    ? series.transportSavings.map((v) => (v >= 0 ? TRANS_FUEL_BORDER_SAVE : TRANS_FUEL_BORDER_COST))
    : [];

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        // Per-year bar colors for buildings/transport (favorable vs unfavorable); use custom legend below chart.
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const y = Number(ctx.parsed.y);
              const datasetLabel = ctx.dataset.label ?? '';
              if (datasetLabel.startsWith('Capital investment')) {
                return `${datasetLabel}: ${currencyShort(y)} (negative outflow)`;
              }
              if (datasetLabel === buildingsOpLegend) {
                return `${datasetLabel}: ${currencyShort(y)} (${y >= 0 ? 'utility savings vs BAU' : 'utility cost increase vs BAU'})`;
              }
              if (datasetLabel === transportOpLegend) {
                return `${datasetLabel}: ${currencyShort(y)} (${y >= 0 ? 'fuel savings vs BAU' : 'fuel cost increase vs BAU'})`;
              }
              if (datasetLabel.startsWith('Net annual cash flow')) {
                return `${datasetLabel}: ${currencyShort(y)} (${y >= 0 ? 'positive net cash flow' : 'negative net cash flow'})`;
              }
              return `${datasetLabel}: ${currencyShort(y)}`;
            },
            afterBody: (items) => {
              const labels = items.map((i) => i.dataset.label ?? '');
              if (!labels.some((l) => l === buildingsOpLegend || l === transportOpLegend)) return [];
              return [
                '',
                'Building electrification “bill impact” is buildings utility only.',
                'This chart adds transport fuel $ so the net operating bar can differ.',
              ];
            },
          },
        },
      },
      scales: {
        x: { title: { display: true, text: 'Year' }, stacked: false },
        y: { title: { display: true, text: '$' }, stacked: false, ticks: { callback: axisTick } },
      },
    }),
    []
  );

  if (!series || series.years.length === 0) return null;
  const hasData =
    series.annualCapex.some((v) => Math.abs(v) >= 1) ||
    series.annualSavings.some((v) => Math.abs(v) >= 1) ||
    series.annualNetCashFlow.some((v) => Math.abs(v) >= 1);
  if (!hasData) return null;

  const data = {
    labels: series.labels,
    datasets: [
      { label: 'Capital investment (−)', data: annualCapexDisplay, backgroundColor: CAPITAL_BAR, yAxisID: 'y' },
      {
        label: buildingsOpLegend,
        data: series.buildingsSavings,
        backgroundColor: buildingsOpColors,
        borderColor: buildingsOpBorders,
        borderWidth: 1,
        yAxisID: 'y',
      },
      {
        label: transportOpLegend,
        data: series.transportSavings,
        backgroundColor: transportOpColors,
        borderColor: transportOpBorders,
        borderWidth: 1,
        yAxisID: 'y',
      },
      { label: 'Net annual cash flow', data: series.annualNetCashFlow, backgroundColor: NET_CASH_BAR, yAxisID: 'y' },
    ],
  };

  return (
    <div style={{ marginTop: 16 }}>
      <h5 style={{ marginBottom: 8 }}>Annual Cash Flow</h5>
      <p style={{ fontSize: '0.8125rem', color: FINANCIAL_COLORS.muted, marginBottom: 8 }}>
        <strong>Portfolio (buildings + transport).</strong> Each year shows four bars: capital (rust), buildings utility
        (blue or magenta by sign), transport fuel (teal or amber; hidden when ~$0), net (slate).
      </p>
      <div style={{ height: 260 }}>
        <Bar data={data} options={options} />
      </div>
      <AnnualCashFlowCustomLegend />
    </div>
  );
};

/** C. Cost Breakdown: stacked bar (buildings capital exp., transport capital exp., buildings savings, transport savings). */
export const PortfolioCostBreakdownChart: React.FC<PortfolioCostChartsProps> = ({ buildings, transport }) => {
  const series = usePortfolioSeries(buildings, transport);
  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y' as const,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${currencyShort(Number(ctx.parsed.x))}`,
          },
        },
      },
      scales: {
        x: { stacked: true, ticks: { callback: axisTick } },
        y: { stacked: true },
      },
    }),
    []
  );

  if (!series || series.years.length === 0) return null;

  const totalBuildingsCapex = series.buildingsCapex.reduce((a, b) => a + b, 0);
  const totalTransportCapex = series.transportCapex.reduce((a, b) => a + b, 0);
  const totalBuildingsSavings = series.buildingsSavings.reduce((a, b) => a + b, 0);
  const totalTransportSavings = series.transportSavings.reduce((a, b) => a + b, 0);
  const hasData =
    Math.abs(totalBuildingsCapex) >= 1 ||
    Math.abs(totalTransportCapex) >= 1 ||
    Math.abs(totalBuildingsSavings) >= 1 ||
    Math.abs(totalTransportSavings) >= 1;
  if (!hasData) return null;

  // Sector identity: Buildings = blue, Transport = green (not good/bad)
  const data = {
    labels: ['Capital exp.', 'Savings'],
    datasets: [
      { label: 'Buildings capital exp.', data: [totalBuildingsCapex, 0], backgroundColor: 'rgba(59, 130, 246, 0.85)' },
      { label: 'Transport capital exp.', data: [totalTransportCapex, 0], backgroundColor: 'rgba(13, 148, 136, 0.85)' },
      { label: 'Buildings savings', data: [0, totalBuildingsSavings], backgroundColor: 'rgba(96, 165, 250, 0.85)' },
      { label: 'Transport savings', data: [0, totalTransportSavings], backgroundColor: 'rgba(45, 212, 191, 0.85)' },
    ],
  };

  return (
    <div style={{ marginTop: 16 }}>
      <h5 style={{ marginBottom: 8 }}>Cost Breakdown (Cumulative)</h5>
      <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: 8 }}>Buildings vs transport capital exp. and savings over 2023–2045.</p>
      <div style={{ height: 140 }}>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

/** Transport-only cumulative net cost (line). Shown in Transport tab. */
export const TransportCumulativeNetCostChart: React.FC<{ transport: TransportCostResults | null }> = ({ transport }) => {
  const series = usePortfolioSeries(null, transport);
  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx) => `${currencyShort(Number(ctx.parsed.y))} (negative = net savings)`,
          },
        },
      },
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: { title: { display: true, text: 'Cumulative $' }, ticks: { callback: axisTick } },
      },
    }),
    []
  );
  if (!series || series.years.length === 0) return null;
  const data = {
    labels: series.labels,
    datasets: [
      { label: 'Cumulative net cost (transport)', data: series.cumulativeNetCost, borderColor: 'rgb(124, 58, 237)', backgroundColor: 'rgba(124, 58, 237, 0.1)', fill: true, tension: 0.2, pointRadius: 2 },
    ],
  };
  return (
    <div style={{ marginTop: 16 }}>
      <h5 style={{ marginBottom: 8 }}>Transport cumulative net cost</h5>
      <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: 8 }}>EV capital exp. − fuel savings by year. Negative = net savings.</p>
      <div style={{ height: 240 }}>
        <Line data={data} options={options} />
      </div>
    </div>
  );
};
